/**
 * 文字単位の差分 (共通接頭辞 / 共通接尾辞方式) (マスタ変更履歴 SBI 7)。
 *
 * 変更前と変更後の文字列から、先頭と末尾の共通部分を取り除き、残った中央を「変更部分」とする。
 * LCS のような最小編集の差分ではない。離れた 2 か所を変更した場合は、間を含めた 1 つの範囲に
 * なる (強調が広めに出るだけで、強調の外が変わっていることはない)。
 *
 * 純粋関数で、依存はない。HTML を作らない (呼び出し側が React の要素として描画する) ため、
 * 文字列に `<` や `&` が含まれていてもエスケープは不要で、タグとして解釈されることもない。
 *
 * ⚠️ サロゲートペア (絵文字など) を途中で分割しない。UTF-16 のコード単位で比べると、
 * 上位 / 下位サロゲートが同じ別の文字 (😀 と 😁) で境界がペアの途中に来てしまう。
 * 境界がペアの途中なら、共通部分を 1 つ減らして文字全体を変更部分に含める。
 */

export interface AffixDiff {
  /** 先頭の共通部分 */
  prefix: string;
  /** 変更前の変更部分 (空なら、変更後に挿入されただけ) */
  beforeChanged: string;
  /** 変更後の変更部分 (空なら、変更前から削除されただけ) */
  afterChanged: string;
  /** 末尾の共通部分 */
  suffix: string;
}

function isHighSurrogate(code: number): boolean {
  return code >= 0xd800 && code <= 0xdbff;
}

function isLowSurrogate(code: number): boolean {
  return code >= 0xdc00 && code <= 0xdfff;
}

/** 共通接頭辞の長さ (UTF-16 のコード単位)。ペアの途中で終わるなら 1 つ戻す。 */
function commonPrefixLength(a: string, b: string): number {
  const limit = Math.min(a.length, b.length);
  let i = 0;
  while (i < limit && a.charCodeAt(i) === b.charCodeAt(i)) i += 1;
  // 直前が上位サロゲートで終わっていれば、その文字の下位側は異なる (または片方が終端)。
  if (i > 0 && isHighSurrogate(a.charCodeAt(i - 1))) i -= 1;
  return i;
}

/**
 * 共通接尾辞の長さ (UTF-16 のコード単位)。`maxLength` を超えない (接頭辞と重ならないため)。
 * ペアの途中から始まるなら 1 つ戻す。
 */
function commonSuffixLength(a: string, b: string, maxLength: number): number {
  const limit = Math.min(a.length, b.length, maxLength);
  let i = 0;
  while (i < limit && a.charCodeAt(a.length - 1 - i) === b.charCodeAt(b.length - 1 - i)) i += 1;
  // 共通部分の先頭が下位サロゲートなら、その文字の上位側は異なる (または範囲外)。
  if (i > 0 && isLowSurrogate(a.charCodeAt(a.length - i))) i -= 1;
  return i;
}

/** 2 つの文字列を、共通接頭辞・共通接尾辞・変更部分に分ける。 */
export function diffByAffix(before: string, after: string): AffixDiff {
  const prefixLength = commonPrefixLength(before, after);
  const suffixLength = commonSuffixLength(
    before,
    after,
    Math.min(before.length, after.length) - prefixLength,
  );
  return {
    prefix: before.slice(0, prefixLength),
    beforeChanged: before.slice(prefixLength, before.length - suffixLength),
    afterChanged: after.slice(prefixLength, after.length - suffixLength),
    suffix: before.slice(before.length - suffixLength),
  };
}

/**
 * 画面で文字単位の強調をかける値だけ差分を返す。対象外なら null。
 *
 * 対象は「両方が空でない文字列で、内容が違う」場合だけ。
 * - null / undefined / 空文字は、「(なし)」「(空)」という表示そのものが変更なので強調しない
 *   (作成・削除では値の全体が変更部分で、強調しても情報が増えない)。
 * - 数値・真偽値・配列・オブジェクトは、文字列としての差分に意味がないので対象にしない。
 */
export function diffTextValues(before: unknown, after: unknown): AffixDiff | null {
  if (typeof before !== 'string' || typeof after !== 'string') return null;
  if (before === '' || after === '') return null;
  if (before === after) return null;
  return diffByAffix(before, after);
}
